import { useState } from "react";
import { Shuffle } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

type CardConnectionResponse = components["schemas"]["CardConnectionResponse"];
type CardTransactionStatus = components["schemas"]["CardTransactionStatus"];

export interface SimulateTransactionDialogProps {
  card: CardConnectionResponse | null;
  onOpenChange: (open: boolean) => void;
  onCreated: (transaction: components["schemas"]["CardTransactionResponse"]) => void;
}

// Goes through the real spend path. Every field is optional: the server fills in a plausible purchase.
export function SimulateTransactionDialog({ card, onOpenChange, onCreated }: SimulateTransactionDialogProps) {
  const api = useDolfinClient();
  const [merchant, setMerchant] = useState("");
  const [amount, setAmount] = useState("");
  const [mcc, setMcc] = useState("");
  const [status, setStatus] = useState<CardTransactionStatus>("Settled");
  const [saving, setSaving] = useState(false);

  function reset() {
    setMerchant("");
    setAmount("");
    setMcc("");
    setStatus("Settled");
  }

  async function submit(random: boolean) {
    if (!card?.id) return;

    const body: components["schemas"]["SimulateCardTransactionRequest"] = random ? {} : { status };
    if (!random) {
      if (merchant.trim()) body.merchant = merchant.trim();
      if (amount.trim()) {
        const value = Number(amount);
        if (!Number.isFinite(value) || value <= 0) {
          toast.error("Amount must be greater than 0.");
          return;
        }
        body.amount = value;
      }
      if (mcc.trim()) {
        if (!/^\d{4}$/.test(mcc.trim())) {
          toast.error("Category code must be four digits.");
          return;
        }
        body.mcc = mcc.trim();
      }
    }

    setSaving(true);
    const { data, error } = await api.POST("/v1/card-connections/{id}/simulated-transactions", {
      params: { path: { id: card.id } },
      body,
    });
    setSaving(false);

    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't create the transaction."));
      return;
    }

    toast.success(`${data.descriptor ?? "Purchase"} added to the card.`);
    reset();
    onOpenChange(false);
    onCreated(data);
  }

  return (
    <Dialog open={card !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a transaction</DialogTitle>
          <DialogDescription>
            Make a purchase on the sample card {card?.last4 ? `ending ${card.last4}` : ""}. It becomes an
            expense exactly like real card spend. Leave anything blank to have it picked for you.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="sim-merchant">Merchant</Label>
            <Input
              id="sim-merchant"
              placeholder="e.g. Pret A Manger"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sim-amount">Amount</Label>
              <Input
                id="sim-amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g. 12.50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sim-mcc">Category code</Label>
              <Input
                id="sim-mcc"
                inputMode="numeric"
                maxLength={4}
                placeholder="e.g. 5814"
                value={mcc}
                onChange={(e) => setMcc(e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as CardTransactionStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Settled">Settled</SelectItem>
                <SelectItem value="Pending">Pending (authorised, not yet settled)</SelectItem>
                <SelectItem value="Declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <Button variant="outline" disabled={saving} onClick={() => submit(true)}>
            <Shuffle className="size-4" /> Random purchase
          </Button>
          <Button disabled={saving} onClick={() => submit(false)}>
            {saving ? <Spinner className="size-4" /> : null} Add transaction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
